import React from "react";

import { Box, Stack, Typography, SxProps } from "@mui/material";

import SettingCheckbox from "./SettingCheckbox";

interface SelectAllMultiSelectAllItem {
  label: string;
  value?: string;
  disabled?: boolean;
  isDefault?: boolean;
  recommended?: boolean;
}

export interface SettingMultiSelectItem {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SettingMultiSelectProps {
  title?: string;
  subtitle?: string;
  /** Disables all options regardless of whether any individual item is enabled */
  disabled?: boolean;
  items: SettingMultiSelectItem[];
  value?: string[];
  onChange: (value: string[]) => void;
  selectAll: SelectAllMultiSelectAllItem;
  sx?: SxProps;
}

const SettingMultiSelect = ({
  disabled,
  title,
  subtitle,
  items,
  value,
  selectAll,
  onChange,
  sx,
}: SettingMultiSelectProps) => {
  const allItemsChecked = items.length === value?.length;

  const handleAllChange = (event: React.SyntheticEvent, checked: boolean) => {
    const newValue = checked ? items.map((item) => item.value) : [];
    onChange(newValue);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    itemValue: string,
    checked: boolean
  ) => {
    const newValue = new Set(value ? [...value] : []);
    checked ? newValue.add(itemValue) : newValue.delete(itemValue);
    onChange(Array.from(newValue));
  };

  return (
    <Box sx={{ ...sx }}>
      <Stack>
        {title && (
          <Typography variant="h3" sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="bodyPrimaryMed" sx={{ mb: 1, fontWeight: 500 }}>
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Stack sx={{ ml: 1 }}>
        <SettingCheckbox
          {...selectAll}
          disabled={disabled || selectAll.disabled}
          checked={allItemsChecked}
          onChange={handleAllChange}
        />
        <Stack sx={{ pl: 4 }}>
          {items.map((item) => (
            <SettingCheckbox
              key={item.label}
              {...item}
              checked={value?.includes(item.value)}
              disabled={disabled || item.disabled}
              onChange={(event, checked) =>
                handleChange(event, item.value, checked)
              }
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default SettingMultiSelect;
